import React, {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import { useHistory } from 'react-router-dom';
import Modal from '../../components/Modal';
import { ApiError } from '../../api/request';
import { useCategoryTree } from '../../components/MaterialFieldInputs';
import { toast } from '../../utils/toast';
import { useRowDragSort } from '../../utils/useRowDragSort';
import {
    dupTitleSet,
    EMPTY_TIP_FILTER,
    filterTips,
    listAllTips,
    listOwnerTips,
    mountTip,
    ownerMountTargets,
    ownerRefCount,
    saveOwnerTipOrder,
    scopeText,
    Tip,
    tipFilterCount,
    TipFilterState,
    TipOwner,
    tipOwnerLabel,
    unmountOwnerTip,
} from '../../api/tips';
import { BulkMountModal, TipForm, TipIcon, TipTypeTag } from './TipWidgets';
import { confirmDialog, alertDialog } from '../../utils/dialog';
import { useCan } from '../../store';
import SearchSelect from '../../components/SearchSelect';
import { zhOptions } from '../../utils/searchOptions';
import { rowClick } from '../../utils/rowNav';
import { CRAFT_OPTS, LIGHT_OPTS, TIP_TYPE_OPTS } from './selectOptions';

// 挂载对象 (材料 / 设备) 的加工小贴士区: 挂载与排序操作面.
// 条目内容单源存于小贴士库, 这里只做引用与排序; 唯一添加入口是「＋ 添加小贴士」.
// 材料详情页 (MaterialTipsCard) 与设备贴士弹窗 (DeviceTipsModal) 共用本组件 ——
// 两个维度的交互完全一致, 差异只有业务键与文案, 收在 TipOwner 与 api/tips 的适配函数里.


function errMsg(e: unknown): string {
    return e instanceof ApiError ? e.message : String(e);
}

// ---- 添加小贴士弹窗内容 (单层弹窗, 搜索优先) -------------------------------
// 顶层组件 (不能定义在卡片函数体内, 否则每次渲染重挂导致输入框失焦).

interface AddTipPanelProps {
    /** 挂载对象 */
    owner: TipOwner;
    /** 已挂载在当前对象的条目 (候选中排除) */
    mountedCodes: Set<string>;
    allTips: Tip[];
    onCancel: () => void;
    /** 勾选添加完成 */
    onMounted: () => void;
    /** 「＋ 新建条目「{搜索词}」」→ 同一弹窗切到表单 */
    onCreateNew: (presetTitle: string) => void;
}

function AddTipPanel({
    owner,
    mountedCodes,
    allTips,
    onCancel,
    onMounted,
    onCreateNew,
}: AddTipPanelProps) {
    const canCreate = useCan('tip', 'create');
    // 挂载 / 卸载 / 排序是材料或设备侧的编辑 (2026-09-13): 按挂载对象判 update
    const canUpdate = useCan(owner.kind, 'update');
    const tree = useCategoryTree();
    const [q, setQ] = useState('');
    const [filter, setFilter] = useState<TipFilterState>(EMPTY_TIP_FILTER);
    const [sel, setSel] = useState<Set<string>>(new Set());
    const [busy, setBusy] = useState(false);

    const dupTitles = useMemo(() => dupTitleSet(allTips), [allTips]);
    const cat1Node = tree.find((t) => t.categoryCode === filter.cat1);
    const cat1Names = cat1Node ? [cat1Node.nameZh, cat1Node.categoryCode] : [];
    const ownerZh = tipOwnerLabel(owner.kind);

    // 候选集: 已挂载的不再出现 + 筛选 + 搜索
    const list = useMemo(
        () =>
            filterTips(
                allTips.filter((t) => !mountedCodes.has(t.tipCode)),
                filter,
                cat1Names,
                q,
            ),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [allTips, mountedCodes, filter, q, tree],
    );
    const nf = tipFilterCount(filter);

    const onConfirm = async () => {
        if (sel.size === 0) {
            toast('未选择任何条目');
            return;
        }
        setBusy(true);
        try {
            await Promise.all(
                Array.from(sel).map((code) =>
                    mountTip(code, ownerMountTargets(owner)),
                ),
            );
            toast(`已添加 ${sel.size} 条（追加在末尾）`);
            onMounted();
        } catch (e) {
            alertDialog(`添加失败: ${errMsg(e)}`);
        } finally {
            setBusy(false);
        }
    };

    return (
        <div>
            <input
                className="inp"
                placeholder="输入操作名搜索现有条目，或直接新建"
                value={q}
                onChange={(e) => setQ(e.target.value)}
            />
            <div className="flex flex-wrap items-center gap-2 mt-3">
                <SearchSelect
                    className="w-28"
                    value={filter.type}
                    onChange={(v) => setFilter({ ...filter, type: v })}
                    placeholder="全部类型"
                    options={TIP_TYPE_OPTS}
                    clearable
                />
                <SearchSelect
                    className="w-28"
                    value={filter.light}
                    onChange={(v) => setFilter({ ...filter, light: v })}
                    placeholder="全部光源"
                    options={LIGHT_OPTS}
                    clearable
                />
                <SearchSelect
                    className="w-28"
                    value={filter.craft}
                    onChange={(v) => setFilter({ ...filter, craft: v })}
                    placeholder="全部工艺"
                    options={CRAFT_OPTS}
                    clearable
                />
                <SearchSelect
                    className="w-36"
                    value={filter.cat1}
                    onChange={(v) => setFilter({ ...filter, cat1: v })}
                    placeholder="全部一级分类"
                    options={zhOptions(tree, (t) => t.categoryCode, (t) => t.nameZh)}
                    clearable
                />
                {nf > 0 && (
                    <button
                        type="button"
                        className="chip"
                        onClick={() => setFilter(EMPTY_TIP_FILTER)}
                    >
                        清空筛选 {nf}
                    </button>
                )}
            </div>
            <div className="text-xs font-semibold text-slate-600 mt-4 mb-2">
                库中匹配（{list.length}）
                {(filter.light || filter.craft) && (
                    <span className="text-slate-400 font-normal ml-1">
                        含「全部光源 /
                        全部工艺」的条目（它们在任何条件下都适用）
                    </span>
                )}
            </div>
            <div style={{ maxHeight: '38vh', overflow: 'auto' }}>
                {list.length === 0 ? (
                    <div className="text-center text-slate-400 text-sm py-6">
                        没有匹配的条目
                    </div>
                ) : (
                    list.map((t) => (
                        <label
                            key={t.tipCode}
                            className="flex items-start gap-2.5 px-3 py-2.5 border border-slate-100 rounded-lg mb-1.5 hover:bg-slate-50 cursor-pointer"
                        >
                            <input
                                type="checkbox"
                                className="mt-1"
                                checked={sel.has(t.tipCode)}
                                onChange={() =>
                                    setSel((prev) => {
                                        const next = new Set(prev);
                                        if (next.has(t.tipCode))
                                            next.delete(t.tipCode);
                                        else next.add(t.tipCode);
                                        return next;
                                    })
                                }
                            />
                            <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 flex-wrap">
                                    <TipTypeTag type={t.type} />
                                    <TipIcon icon={t.icon} />
                                    <span className="text-[13px] font-medium">
                                        {t.title}
                                    </span>
                                    <span className="text-[11px] text-slate-400 font-mono">
                                        {t.tipCode}
                                    </span>
                                    {dupTitles.has(t.title) && (
                                        <span className="tag bg-amber-50 text-amber-700">
                                            同名
                                        </span>
                                    )}
                                </div>
                                <div className="text-xs text-slate-500 mt-0.5 leading-relaxed">
                                    {t.description}
                                </div>
                                <div className="text-[11px] text-slate-400 mt-0.5">
                                    {scopeText(t)} · 已挂 {t.refCount} 个材料 /{' '}
                                    {t.deviceRefCount} 台设备
                                    {t.usedCats.length > 0 &&
                                        ` · ${t.usedCats.join('、')}`}
                                </div>
                            </div>
                        </label>
                    ))
                )}
            </div>
            <div className="flex items-center gap-3 my-4">
                <div className="flex-1 border-t border-slate-100" />
                <span className="text-[11px] text-slate-400">或</span>
                <div className="flex-1 border-t border-slate-100" />
            </div>
            <button
                type="button"
                className="w-full text-sm px-4 py-2.5 rounded border border-brand-200 text-brand-600 hover:bg-brand-50"
                onClick={() => onCreateNew(q)}
                hidden={!canCreate}
            >
                ＋ 新建条目{q ? `「${q}」` : ''}
            </button>
            <div className="flex items-center justify-between mt-4 pt-3 border-t border-slate-100">
                <span className="text-xs text-slate-500">
                    已选 <b>{sel.size}</b> 条
                </span>
                <div className="flex gap-2">
                    <button
                        type="button"
                        className="btn-ghost"
                        disabled={busy}
                        onClick={onCancel}
                    >
                        取消
                    </button>
                    <button
                        type="button"
                        className={`btn-primary ${sel.size === 0 ? 'opacity-40 pointer-events-none' : ''}`}
                        disabled={busy}
                        onClick={onConfirm}
                        hidden={!canUpdate}
                    >
                        添加到本{ownerZh}
                    </button>
                </div>
            </div>
        </div>
    );
}

// ---- 卡片本体 -------------------------------------------------------------

// 只有 新建 / 批量挂载 两种表单形态 —— 编辑条目内容一律去小贴士库 (2026-08-28:
// 同一条目能在材料页、设备页、库页三处改, 口径太容易漂, 收敛到库这一个入口)
type CardModal =
    | { view: 'add' }
    | { view: 'form'; presetTitle?: string }
    | { view: 'bulk'; tipCode: string; title: string };

export interface TipsMountCardProps {
    owner: TipOwner;
    /** 内嵌在弹窗里时去掉卡片外框 (设备侧) */
    bare?: boolean;
    /** 列表默认收起, 点「展开」才显示条目 (材料详情页: 条目多时占篇幅) */
    collapsible?: boolean;
}

function TipsMountCard({ owner, bare, collapsible }: TipsMountCardProps) {
    // 挂载 / 卸载 / 排序是材料或设备侧的编辑 (2026-09-13): 按挂载对象判 update
    const canUpdate = useCan(owner.kind, 'update');
    const history = useHistory();
    const ownerZh = tipOwnerLabel(owner.kind);
    const ownerKey = owner.key;

    const [tips, setTips] = useState<Tip[]>([]);
    const [allTips, setAllTips] = useState<Tip[]>([]);
    const [modal, setModal] = useState<CardModal | null>(null);
    const [expanded, setExpanded] = useState(!collapsible);
    const rowsRef = useRef<HTMLDivElement>(null);

    const reload = useCallback(async () => {
        try {
            const [mine, all] = await Promise.all([
                listOwnerTips(owner),
                listAllTips(),
            ]);
            setTips(mine || []);
            setAllTips(all || []);
        } catch (e) {
            console.error('加载小贴士失败', e);
        }
        // owner 是每次渲染新建的对象字面量, 依赖只取业务键 (kind 与 key 一一对应)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [owner.kind, ownerKey]);

    useEffect(() => {
        void reload();
    }, [reload]);

    const dupTitles = useMemo(() => dupTitleSet(allTips), [allTips]);
    const mountedCodes = useMemo(
        () => new Set(tips.map((t) => t.tipCode)),
        [tips],
    );
    const hasSafety = tips.some((t) => t.type === 'safety');
    // 收起态只藏条目列表与底部说明; 空列表照常显示「暂无」占位
    const toggleable = !!collapsible && tips.length > 0;
    const folded = toggleable && !expanded;

    // ---- 拖拽排序: 整行是拖拽热区 (原来只有行首 ⠿ 那几像素, 太难瞄); 松手按当前 DOM 顺序落库 ----
    useRowDragSort(
        rowsRef,
        '.tiprow',
        (order) => {
            setTips((prev) => {
                const byCode = new Map(prev.map((t) => [t.tipCode, t]));
                const next = order
                    .map((c) => byCode.get(c))
                    .filter((t): t is Tip => !!t);
                return next.length === prev.length ? next : prev;
            });
            saveOwnerTipOrder(owner, order)
                .then(() => toast('顺序已保存'))
                .catch((e) => {
                    alertDialog(`保存顺序失败: ${errMsg(e)}`);
                    void reload();
                });
        },
        // expanded: 收起时列表不渲染, 展开后要对新挂出的行重新绑定拖拽
        [tips, expanded, owner.kind, ownerKey, reload],
    );

    // ---- 行操作 -----------------------------------------------------------

    const onRemove = async (t: Tip) => {
        if (
            !(await confirmDialog(
                `确认从「${owner.name}」移除「${t.title}」？\n（条目保留在小贴士库，仅解除本${ownerZh}挂载）`,
            ))
        ) {
            return;
        }
        try {
            await unmountOwnerTip(owner, t.tipCode);
            toast('已移除挂载');
            void reload();
        } catch (e) {
            alertDialog(`移除失败: ${errMsg(e)}`);
        }
    };

    return (
        <div className={bare ? '' : 'card p-4'}>
            {/* 可折叠时整行 (含空白处) 点击即展开/收起; 「添加」按钮由 rowClick 跳过, 不会误触 */}
            <div
                className={`flex items-center justify-between ${folded ? '' : 'mb-3'} ${
                    toggleable
                        ? 'cursor-pointer select-none hover:bg-slate-50 rounded -mx-2 px-2 -my-1 py-1'
                        : ''
                }`}
                onClick={
                    toggleable
                        ? rowClick(() => setExpanded((v) => !v))
                        : undefined
                }
                aria-expanded={toggleable ? expanded : undefined}
            >
                <div className="text-sm font-semibold">
                    加工小贴士（{tips.length}）
                    <span className="text-[11px] text-slate-400 font-normal ml-1">
                        拖拽整行调整顺序
                        {hasSafety &&
                            '；安全提示按顺序取第一条常显，最要紧的拖到最前'}
                    </span>
                </div>
                <div className="flex items-center gap-3">
                    {toggleable && (
                        <span className="text-xs text-brand-600">
                            {expanded ? '收起 ▴' : '展开 ▾'}
                        </span>
                    )}
                    <button
                        type="button"
                        className="text-xs px-3 py-1.5 rounded bg-brand-500 text-white hover:bg-brand-600"
                        onClick={() => setModal({ view: 'add' })}
                        hidden={!canUpdate}
                    >
                        ＋ 添加小贴士
                    </button>
                </div>
            </div>
            {folded ? null : tips.length > 0 ? (
                <div ref={rowsRef}>
                    {tips.map((t, i) => (
                        <div
                            key={t.tipCode}
                            className="tiprow"
                            style={{ cursor: 'grab' }}
                            title="拖拽整行调整顺序"
                            data-sort-key={t.tipCode}
                        >
                            {/* ⠿ 只作视觉暗示, 热区是整行 (小手柄太难瞄) */}
                            <span className="grip">⠿</span>
                            <span className="text-[11px] text-slate-300 w-4 pt-0.5">
                                {i + 1}
                            </span>
                            <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 flex-wrap">
                                    <TipTypeTag type={t.type} />
                                    <TipIcon icon={t.icon} />
                                    <span className="text-[13px] font-medium">
                                        {t.title}
                                    </span>
                                    <span className="text-[11px] text-slate-400 font-mono">
                                        {t.tipCode}
                                    </span>
                                    {ownerRefCount(t, owner.kind) > 1 && (
                                        <span className="tag bg-slate-100 text-slate-500">
                                            共 {ownerRefCount(t, owner.kind)} 个
                                            {ownerZh}在用
                                        </span>
                                    )}
                                    {/* 另一维度也在用时提示一下: 去库里改内容会同时影响那一侧 */}
                                    {owner.kind === 'material' &&
                                        t.deviceRefCount > 0 && (
                                            <span className="tag bg-slate-100 text-slate-500">
                                                另挂 {t.deviceRefCount} 台设备
                                            </span>
                                        )}
                                    {owner.kind === 'device' &&
                                        t.refCount > 0 && (
                                            <span className="tag bg-slate-100 text-slate-500">
                                                另挂 {t.refCount} 个材料
                                            </span>
                                        )}
                                </div>
                                <div className="text-xs text-slate-500 mt-1 leading-relaxed">
                                    {t.description}
                                </div>
                                <div className="text-[11px] text-slate-400 mt-1">
                                    {scopeText(t)}
                                    {dupTitles.has(t.title) && (
                                        <span className="text-amber-600">
                                            {' '}
                                            · 库中有同名条目，靠说明区分
                                        </span>
                                    )}
                                </div>
                            </div>
                            <div className="whitespace-nowrap text-xs pt-0.5">
                                <a
                                    className="text-brand-600 cursor-pointer hover:underline"
                                    title="到小贴士库查看/编辑该条目内容"
                                    onClick={() =>
                                        history.push(
                                            `/tips?edit=${encodeURIComponent(t.tipCode)}`,
                                        )
                                    }
                                >
                                    跳转到小贴士 →
                                </a>
                                <a
                                    className="text-rose-500 cursor-pointer hover:underline ml-3"
                                    onClick={() => onRemove(t)}
                                    hidden={!canUpdate}
                                >
                                    移除
                                </a>
                            </div>
                        </div>
                    ))}
                </div>
            ) : (
                <div className="text-center text-slate-400 text-sm py-7 border border-dashed border-slate-200 rounded-lg">
                    该{ownerZh}暂无小贴士
                </div>
            )}
            <div className="text-[11px] text-slate-400 mt-2" hidden={folded}>
                改内容点「跳转到小贴士」到<b>小贴士库</b>
                改（条目内容单源在库里维护，改一处所有引用同步生效）；
                「移除」只解除本{ownerZh}
                的挂载，条目仍在小贴士库中（其他材料/设备不受影响）。
            </div>

            {/* 添加弹窗 / 条目表单: 同一个 Modal 内以视图切换, 不卸载重挂 */}
            {modal && modal.view !== 'bulk' && (
                <Modal
                    open
                    width={modal.view === 'add' ? 760 : 720}
                    title={
                        modal.view === 'add'
                            ? `添加加工小贴士 · ${owner.name}`
                            : '新建条目'
                    }
                    subtitle={
                        modal.view === 'add'
                            ? '先搜库里有没有——同名条目靠「补充说明」区分'
                            : undefined
                    }
                    onClose={() => setModal(null)}
                >
                    {modal.view === 'add' ? (
                        <AddTipPanel
                            owner={owner}
                            mountedCodes={mountedCodes}
                            allTips={allTips}
                            onCancel={() => setModal(null)}
                            onMounted={() => {
                                setModal(null);
                                void reload();
                            }}
                            onCreateNew={(presetTitle) =>
                                setModal({ view: 'form', presetTitle })
                            }
                        />
                    ) : (
                        <TipForm
                            presetTitle={modal.presetTitle}
                            owner={owner}
                            onCancel={() => setModal(null)}
                            onBack={() => setModal({ view: 'add' })}
                            onSaved={(openBulkFor) => {
                                if (openBulkFor) {
                                    setModal({
                                        view: 'bulk',
                                        tipCode: openBulkFor.tipCode,
                                        title: openBulkFor.title,
                                    });
                                } else {
                                    setModal(null);
                                }
                                void reload();
                            }}
                        />
                    )}
                </Modal>
            )}
            {modal?.view === 'bulk' && (
                <BulkMountModal
                    tipCode={modal.tipCode}
                    tipTitle={modal.title}
                    justCreated
                    initialDim={owner.kind}
                    onClose={() => {
                        setModal(null);
                        void reload();
                    }}
                />
            )}
        </div>
    );
}

export default TipsMountCard;
